import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { prospectService } from '../services/api';
import { Prospect } from '../types';

const ProspectQuestionnaire: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [prospect, setProspect] = useState<Prospect | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchProspect = async () => {
      if (!id) return;
      
      try {
        const response = await prospectService.getProspect(parseInt(id));
        setProspect(response.prospect);
      } catch (err) {
        setError('Failed to load prospect details');
      } finally {
        setLoading(false);
      }
    };

    fetchProspect();
  }, [id]);

  const CompactInfoField: React.FC<{ label: string; value: string | undefined | null; placeholder?: string }> = ({ 
    label, 
    value, 
    placeholder = 'Not provided' 
  }) => (
    <div className="flex flex-col sm:flex-row sm:items-center py-3 border-b border-gray-100 last:border-b-0">
      <label className="text-sm font-medium text-gray-700 sm:w-1/3 mb-1 sm:mb-0">
        {label}
      </label>
      <div className="sm:w-2/3 text-sm text-gray-900">
        {value || <span className="text-gray-500 italic">{placeholder}</span>}
      </div>
    </div>
  );

  const CompactTextAreaField: React.FC<{ label: string; value: string | undefined | null; placeholder?: string }> = ({ 
    label, 
    value, 
    placeholder = 'Not provided' 
  }) => (
    <div className="py-3 border-b border-gray-100 last:border-b-0">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <div className="text-sm text-gray-900 whitespace-pre-wrap bg-gray-50 p-3 rounded border">
        {value || <span className="text-gray-500 italic">{placeholder}</span>}
      </div>
    </div>
  );

  const formatValue = (value: string | undefined | null, type?: 'date' | 'select'): string => {
    if (!value) return '';
    
    if (type === 'date') {
      return new Date(value).toLocaleDateString();
    }
    
    if (type === 'select') {
      // Format common select values
      return value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    
    return value;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-6 py-2"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!prospect) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Prospect Not Found</h2>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-6 py-2"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Initial Interest - {prospect.first_name} {prospect.last_name}
            </h1>
            <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">
              Home page registration and first qualification step
            </p>
          </div>
          <button
            onClick={() => navigate(`/admin/prospects/${id}`)}
            className="text-gray-600 hover:text-gray-800 font-medium text-sm flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Back to Prospect Detail
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
            {error}
          </div>
        )}

        <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50">
                Program interest qualification
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Information captured from home page and first qualification step
              </p>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Contact Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                Contact Information
              </h3>
              <div className="space-y-0">
                <CompactInfoField label="Name" value={`${prospect.first_name} ${prospect.last_name}`} />
                <CompactInfoField label="Email" value={prospect.email || '-'} />
                <CompactInfoField label="Phone" value={prospect.phone || '-'} />
              </div>
            </div>

            {/* Program Interest */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Program Selection
              </h3>
              <div className="space-y-0">
                <CompactInfoField label="Program Interest" value={formatValue(prospect.program_interest, 'select')} />
              </div>
            </div>

            {/* Interest Assessment */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                Interest Assessment
              </h3>
              <div className="space-y-0">
                <CompactInfoField label="Commitment Level" value={formatValue(prospect.commitment_level, 'select')} />
                <CompactInfoField label="Timeline" value={formatValue(prospect.timeline, 'select')} />
                <CompactInfoField label="How did you hear about us?" value={formatValue(prospect.how_did_you_hear, 'select')} />
                <CompactTextAreaField 
                  label="Program Motivation" 
                  value={prospect.program_motivation} 
                  placeholder="What interests them about studying abroad"
                />
              </div>
            </div>
          </div>

          {/* Compact Footer */}
          <div className="bg-gray-100 px-6 py-3 border-t border-gray-200">
            <div className="flex flex-wrap justify-between items-center text-xs text-gray-600 gap-2">
              <span>Stage: <strong className="text-gray-900">{formatValue(prospect.current_stage, 'select')}</strong></span>
              <span>Created: <strong className="text-gray-900">{new Date(prospect.created_at).toLocaleDateString()}</strong></span>
              {prospect.updated_at && (
                <span>Updated: <strong className="text-gray-900">{new Date(prospect.updated_at).toLocaleDateString()}</strong></span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProspectQuestionnaire;
